import formatClockTime from './formatClockTime';

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
const DAYS_IN_WEEK = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function formatCalendarDay(date: Date, now: Date): string {
  const dayDiff = Math.round((startOfLocalDay(now) - startOfLocalDay(date)) / MS_PER_DAY);
  if (dayDiff <= 0) {
    return 'Today';
  }
  if (dayDiff === 1) {
    return 'Yesterday';
  }
  if (dayDiff < DAYS_IN_WEEK) {
    return WEEKDAY_NAMES[date.getDay()];
  }
  // Older than a week: a bare weekday would be ambiguous, so name the date —
  // dropping the year unless it differs from the viewer's current year.
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(
    'en-US',
    sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' }
  );
}

/**
 * Format a session-gap divider label for the chat transcript — the date stamp
 * that sits centered on the rule drawn above the first message of a new session.
 *
 * Pairs a calendar-relative day with a lowercase clock time:
 *
 * | when                     | example                  |
 * | ------------------------ | ------------------------ |
 * | today                    | `Today at 3:20 pm`       |
 * | yesterday                | `Yesterday at 3:20 pm`   |
 * | 2–6 days ago (this week) | `Friday at 3:20 pm`      |
 * | 7+ days ago              | `May 23 at 3:20 pm`      |
 * | 7+ days, different year  | `May 23, 2024 at 3:20 pm`|
 *
 * Day buckets are computed from local-midnight boundaries, so the today/
 * yesterday label is stable across re-renders within the same day rather than
 * flipping on a rolling 24h window. `now` is injectable for deterministic tests.
 */
export default function formatSessionDivider(date: Date, now: Date = new Date()): string {
  return `${formatCalendarDay(date, now)} at ${formatClockTime(date)}`;
}
